import React, { useEffect, useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '../components/common/Button'
import { Card } from '../components/common/Card'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { useAuth } from '../context/AuthContext'
import { APP_USER_ROLES } from '../lib/config/status-config'
import { TASK_STATUSES, TASK_STATUS_LABELS } from '../lib/config/project-config'
import { mutations } from '../lib/supabase/mutations'
import { queries } from '../lib/supabase/queries'
import { Profile, RelatedAppRecord } from '../lib/types/database.types'

type RelatedAppDraft = {
  name: string
  next_release_version: string
  responsible_person: string
}

type UserDraft = Pick<
  Profile,
  'role' | 'quote_first' | 'is_tester' | 'is_2nd_tester' | 'is_coder' | 'is_task_approver' | 'is_finance_approver'
>

const inputClassName = 'w-full rounded-lg border border-gray-300 bg-stone-50 px-3 py-2 text-sm text-gray-800'
const selectClassName = 'w-full rounded-lg border border-gray-300 bg-stone-50 px-3 py-2 text-sm text-gray-800'

const createRelatedAppDraft = (app: RelatedAppRecord): RelatedAppDraft => ({
  name: app.name,
  next_release_version: app.next_release_version || '',
  responsible_person: app.responsible_person || '',
})

const createUserDraft = (profile: Profile): UserDraft => ({
  role: profile.role,
  quote_first: profile.quote_first,
  is_tester: profile.is_tester,
  is_2nd_tester: profile.is_2nd_tester,
  is_coder: profile.is_coder,
  is_task_approver: profile.is_task_approver,
  is_finance_approver: profile.is_finance_approver,
})

export const ManagePage: React.FC = () => {
  const queryClient = useQueryClient()
  const { profile: currentProfile } = useAuth()
  const [relatedAppDrafts, setRelatedAppDrafts] = useState<Record<string, RelatedAppDraft>>({})
  const [userDrafts, setUserDrafts] = useState<Record<string, UserDraft>>({})
  const [newAppName, setNewAppName] = useState('')
  const [newAppNextRelease, setNewAppNextRelease] = useState('')
  const [newAppResponsiblePerson, setNewAppResponsiblePerson] = useState('')
  const [relatedAppsError, setRelatedAppsError] = useState<string | null>(null)
  const [usersError, setUsersError] = useState<string | null>(null)
  const [activeRelatedAppId, setActiveRelatedAppId] = useState<string | null>(null)
  const [activeUserId, setActiveUserId] = useState<string | null>(null)
  const [statusDefaultDrafts, setStatusDefaultDrafts] = useState<Record<string, string>>({})
  const [statusDefaultsError, setStatusDefaultsError] = useState<string | null>(null)
  const [activeStatus, setActiveStatus] = useState<string | null>(null)

  const relatedAppsQuery = useQuery({
    queryKey: ['related-apps'],
    queryFn: queries.getRelatedApps,
  })

  const profilesQuery = useQuery({
    queryKey: ['profiles', 'all'],
    queryFn: queries.getAllProfiles,
  })

  const statusDefaultsQuery = useQuery({
    queryKey: ['task-status-default-days'],
    queryFn: queries.getTaskStatusDefaultDays,
  })

  useEffect(() => {
    if (!relatedAppsQuery.data) {
      return
    }

    setRelatedAppDrafts(
      Object.fromEntries(relatedAppsQuery.data.map((app) => [app.id, createRelatedAppDraft(app)]))
    )
  }, [relatedAppsQuery.data])

  useEffect(() => {
    if (!profilesQuery.data) {
      return
    }

    setUserDrafts(
      Object.fromEntries(profilesQuery.data.map((item) => [item.id, createUserDraft(item)]))
    )
  }, [profilesQuery.data])

  useEffect(() => {
    if (!statusDefaultsQuery.data) {
      return
    }

    const rowMap = new Map(statusDefaultsQuery.data.map((item) => [item.status, item.default_days]))
    setStatusDefaultDrafts(
      Object.fromEntries(TASK_STATUSES.map((status) => [status, String(rowMap.get(status) ?? 0)]))
    )
  }, [statusDefaultsQuery.data])

  const invalidateManageQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['related-apps'] }),
      queryClient.invalidateQueries({ queryKey: ['profiles'] }),
      queryClient.invalidateQueries({ queryKey: ['task-status-default-days'] }),
      queryClient.invalidateQueries({ queryKey: ['tasks'] }),
      queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
    ])
  }

  const createRelatedAppMutation = useMutation({
    mutationFn: mutations.createRelatedApp,
    onSuccess: async () => {
      await invalidateManageQueries()
      setNewAppName('')
      setNewAppNextRelease('')
      setNewAppResponsiblePerson('')
      setRelatedAppsError(null)
    },
  })

  const updateRelatedAppMutation = useMutation({
    mutationFn: ({ appId, updates }: { appId: string; updates: Parameters<typeof mutations.updateRelatedApp>[1] }) =>
      mutations.updateRelatedApp(appId, updates),
    onSuccess: async () => {
      await invalidateManageQueries()
      setRelatedAppsError(null)
    },
  })

  const updateProfileMutation = useMutation({
    mutationFn: ({ userId, updates }: { userId: string; updates: Parameters<typeof mutations.updateProfile>[1] }) =>
      mutations.updateProfile(userId, updates),
    onSuccess: async () => {
      await invalidateManageQueries()
      setUsersError(null)
    },
  })

  const upsertStatusDefaultsMutation = useMutation({
    mutationFn: ({ status, defaultDays }: { status: string; defaultDays: number }) =>
      mutations.upsertTaskStatusDefaultDays(status, defaultDays),
    onSuccess: async () => {
      await invalidateManageQueries()
      setStatusDefaultsError(null)
    },
  })

  const profileOptions = useMemo(
    () => (profilesQuery.data || []).map((item) => ({
      id: item.id,
      label: item.full_name || item.email || 'Unknown user',
      email: item.email || '',
    })),
    [profilesQuery.data]
  )

  if (currentProfile?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />
  }

  const isLoading = relatedAppsQuery.isLoading || profilesQuery.isLoading || statusDefaultsQuery.isLoading
  const error = relatedAppsQuery.error || profilesQuery.error || statusDefaultsQuery.error

  const handleCreateRelatedApp = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!newAppName.trim()) {
      setRelatedAppsError('App name is required')
      return
    }

    try {
      await createRelatedAppMutation.mutateAsync({
        name: newAppName.trim(),
        current_version: null,
        next_release_version: newAppNextRelease.trim() || null,
        responsible_person: newAppResponsiblePerson || null,
      })
    } catch (err) {
      setRelatedAppsError(err instanceof Error ? err.message : 'Unable to create related app')
    }
  }

  const handleRelatedAppDraftChange = (appId: string, field: keyof RelatedAppDraft, value: string) => {
    setRelatedAppDrafts((current) => ({
      ...current,
      [appId]: {
        ...current[appId],
        [field]: value,
      },
    }))
  }

  const handleSaveRelatedApp = async (appId: string) => {
    const draft = relatedAppDrafts[appId]
    if (!draft?.name.trim()) {
      setRelatedAppsError('App name is required')
      return
    }

    setActiveRelatedAppId(appId)

    try {
      await updateRelatedAppMutation.mutateAsync({
        appId,
        updates: {
          name: draft.name.trim(),
          next_release_version: draft.next_release_version.trim() || null,
          responsible_person: draft.responsible_person || null,
        },
      })
    } catch (err) {
      setRelatedAppsError(err instanceof Error ? err.message : 'Unable to update related app')
    } finally {
      setActiveRelatedAppId(null)
    }
  }

  const handleUserDraftChange = <K extends keyof UserDraft>(userId: string, field: K, value: UserDraft[K]) => {
    setUserDrafts((current) => ({
      ...current,
      [userId]: {
        ...current[userId],
        [field]: value,
      },
    }))
  }

  const handleSaveUser = async (userId: string) => {
    const draft = userDrafts[userId]
    if (!draft) {
      return
    }

    setActiveUserId(userId)

    try {
      await updateProfileMutation.mutateAsync({
        userId,
        updates: draft,
      })
    } catch (err) {
      setUsersError(err instanceof Error ? err.message : 'Unable to update user')
    } finally {
      setActiveUserId(null)
    }
  }

  const handleStatusDefaultDraftChange = (status: string, value: string) => {
    setStatusDefaultDrafts((current) => ({
      ...current,
      [status]: value,
    }))
  }

  const handleSaveStatusDefault = async (status: string) => {
    const rawValue = statusDefaultDrafts[status] ?? '0'
    const parsedValue = Number.parseInt(rawValue, 10)

    if (!Number.isInteger(parsedValue) || parsedValue < 0) {
      setStatusDefaultsError('Default days must be a whole number greater than or equal to 0.')
      return
    }

    setActiveStatus(status)

    try {
      await upsertStatusDefaultsMutation.mutateAsync({
        status,
        defaultDays: parsedValue,
      })
    } catch (err) {
      setStatusDefaultsError(err instanceof Error ? err.message : 'Unable to save status default days')
    } finally {
      setActiveStatus(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1.5 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Manage</h1>
          <p className="text-sm text-gray-600">Admin tools for related apps, release planning, and user capabilities.</p>
        </div>
      </div>

      {isLoading && (
        <Card>
          <div className="flex min-h-[240px] items-center justify-center">
            <LoadingSpinner text="Loading management data..." />
          </div>
        </Card>
      )}

      {error && !isLoading && (
        <Card>
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error instanceof Error ? error.message : 'Failed to load admin data.'}
          </div>
        </Card>
      )}

      {!isLoading && !error && (
        <>
          <Card
            header={
              <div>
                <h2 className="font-semibold text-gray-900">Manage Related Apps</h2>
                <p className="text-sm text-gray-500">Add apps, assign a responsible person, and set the next release version.</p>
              </div>
            }
          >
            <div className="space-y-6">
              <form className="grid gap-4 rounded-xl border border-stone-300 bg-stone-50 p-4 md:grid-cols-4" onSubmit={handleCreateRelatedApp}>
                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-gray-700">App Name</label>
                  <input className={inputClassName} value={newAppName} onChange={(event) => setNewAppName(event.target.value)} />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Next Release</label>
                  <input
                    className={inputClassName}
                    value={newAppNextRelease}
                    onChange={(event) => setNewAppNextRelease(event.target.value)}
                    placeholder="vNext"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Responsible Person</label>
                  <select
                    className={selectClassName}
                    value={newAppResponsiblePerson}
                    onChange={(event) => setNewAppResponsiblePerson(event.target.value)}
                  >
                    <option value="">Unassigned</option>
                    {profileOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-4 flex items-center justify-between gap-3">
                  {relatedAppsError ? <p className="text-sm text-red-700">{relatedAppsError}</p> : <span />}
                  <Button type="submit" isLoading={createRelatedAppMutation.isPending}>Add Related App</Button>
                </div>
              </form>

              <div className="space-y-4">
                {(relatedAppsQuery.data || []).map((app) => {
                  const draft = relatedAppDrafts[app.id] || createRelatedAppDraft(app)
                  const responsiblePersonLabel = profileOptions.find((option) => option.id === app.responsible_person)?.label || 'Unassigned'

                  return (
                    <div key={app.id} className="grid gap-4 rounded-xl border border-stone-300 bg-stone-50 p-4 md:grid-cols-5">
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">App Name</label>
                        <input
                          className={inputClassName}
                          value={draft.name}
                          onChange={(event) => handleRelatedAppDraftChange(app.id, 'name', event.target.value)}
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Current Release</label>
                        <div className="rounded-lg border border-stone-300 bg-stone-100 px-3 py-2 text-sm text-gray-700">
                          {app.current_version || 'Not set'}
                        </div>
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Next Release</label>
                        <input
                          className={inputClassName}
                          value={draft.next_release_version}
                          onChange={(event) => handleRelatedAppDraftChange(app.id, 'next_release_version', event.target.value)}
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Responsible Person</label>
                        <select
                          className={selectClassName}
                          value={draft.responsible_person}
                          onChange={(event) => handleRelatedAppDraftChange(app.id, 'responsible_person', event.target.value)}
                        >
                          <option value="">Unassigned</option>
                          {profileOptions.map((option) => (
                            <option key={option.id} value={option.id}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <p className="mt-1 text-xs text-gray-500">Current: {responsiblePersonLabel}</p>
                      </div>
                      <div className="flex items-end justify-end">
                        <Button
                          type="button"
                          isLoading={activeRelatedAppId === app.id && updateRelatedAppMutation.isPending}
                          onClick={() => handleSaveRelatedApp(app.id)}
                        >
                          Save App
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </Card>

          <Card
            header={
              <div>
                <h2 className="font-semibold text-gray-900">Status Default Due Days</h2>
                <p className="text-sm text-gray-500">Set how many days are auto-added when a task enters each status.</p>
              </div>
            }
          >
            <div className="space-y-4">
              {statusDefaultsError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{statusDefaultsError}</div>
              )}

              {TASK_STATUSES.map((status) => (
                <div key={status} className="grid gap-3 rounded-xl border border-stone-300 bg-stone-50 p-4 md:grid-cols-[minmax(0,1fr)_140px_auto] md:items-end">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{TASK_STATUS_LABELS[status]}</p>
                    <p className="text-xs text-gray-500">Applies when task status changes to {TASK_STATUS_LABELS[status]}.</p>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Default Days</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      className={inputClassName}
                      value={statusDefaultDrafts[status] ?? '0'}
                      onChange={(event) => handleStatusDefaultDraftChange(status, event.target.value)}
                    />
                  </div>
                  <div className="md:text-right">
                    <Button
                      type="button"
                      isLoading={activeStatus === status && upsertStatusDefaultsMutation.isPending}
                      onClick={() => handleSaveStatusDefault(status)}
                    >
                      Save Default
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card
            header={
              <div>
                <h2 className="font-semibold text-gray-900">Manage Users</h2>
                <p className="text-sm text-gray-500">Update roles and capability flags for admins, developers, and request approvers.</p>
              </div>
            }
          >
            <div className="space-y-4">
              {usersError && <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{usersError}</div>}
              {(profilesQuery.data || []).map((userProfile) => {
                const draft = userDrafts[userProfile.id] || createUserDraft(userProfile)

                return (
                  <div key={userProfile.id} className="rounded-xl border border-stone-300 bg-stone-50 p-4 shadow-sm">
                    <div className="mb-4 flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">{userProfile.full_name || 'Unnamed user'}</h3>
                        <p className="text-sm text-gray-500">{userProfile.email || 'No email available'}</p>
                      </div>
                      <Button
                        type="button"
                        isLoading={activeUserId === userProfile.id && updateProfileMutation.isPending}
                        onClick={() => handleSaveUser(userProfile.id)}
                      >
                        Save User
                      </Button>
                    </div>

                    <div className="grid gap-4 md:grid-cols-4">
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Role</label>
                        <select
                          className={selectClassName}
                          value={draft.role}
                          onChange={(event) => handleUserDraftChange(userProfile.id, 'role', event.target.value)}
                        >
                          {APP_USER_ROLES.map((role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ))}
                        </select>
                      </div>

                      {[
                        { key: 'quote_first', label: 'Quote First' },
                        { key: 'is_tester', label: 'Tester' },
                        { key: 'is_2nd_tester', label: '2nd Tester' },
                        { key: 'is_coder', label: 'Coder' },
                        { key: 'is_task_approver', label: 'Task Approver' },
                        { key: 'is_finance_approver', label: 'Finance Approver' },
                      ].map((field) => (
                        <label key={field.key} className="flex items-center gap-3 rounded-lg border border-stone-300 bg-stone-100 px-3 py-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={Boolean(draft[field.key as keyof UserDraft])}
                            onChange={(event) =>
                              handleUserDraftChange(
                                userProfile.id,
                                field.key as keyof UserDraft,
                                event.target.checked as never
                              )
                            }
                          />
                          <span>{field.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          </Card>
        </>
      )}
    </div>
  )
}